// ============================================
// Framer Motion Animation Variants
// ============================================

import type { Transition } from 'framer-motion';

// ============================================
// Variant typing
// ============================================
//
// We deliberately do NOT type these against framer-motion's own `Variants`
// type. Under the `preact/compat` alias (see vite.config.ts) framer's
// `TargetAndTransition` resolves with a leaked `[key: string]: string | number`
// index signature (via preact's CSS/SVG attribute types flowing into
// `TargetProperties`). That index signature collides with the object-typed
// `transition` field, so `satisfies Variants` — and assigning any concrete
// variant to a `variants={...}` prop — fails to type-check for every object
// that has a `transition`. Modelling the handful of properties we actually
// animate keeps authoring fully type-checked (typos and wrong shapes are
// caught here); the single unavoidable cast to framer's `Variants` lives at
// the library boundary in ./index.ts.

type Keyframable<T> = T | T[];

interface AnimationTarget {
  opacity?: number;
  scale?: Keyframable<number>;
  scaleX?: number;
  originX?: number;
  x?: Keyframable<number>;
  y?: number;
  rotateY?: number;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  boxShadow?: Keyframable<string>;
  pointerEvents?: 'none' | 'auto';
  // Either one Transition for the whole target, or per-value transitions.
  transition?: Transition | Record<string, Transition>;
}

type AnimationVariant = AnimationTarget | ((custom: number) => AnimationTarget);

export type AnimationVariants = Record<string, AnimationVariant>;

// ============================================
// Shared Transitions
// ============================================

const easeOut: Transition = { duration: 0.4, ease: [0.33, 1, 0.68, 1] };
const easeInOut: Transition = { duration: 0.25, ease: [0.65, 0, 0.35, 1] };
const spring: Transition = { type: 'spring', stiffness: 400, damping: 25 };

// ============================================
// Card Flip Animation (3D perspective)
// ============================================

export const cardFlip = {
  front: {
    rotateY: 0,
    transition: easeOut
  },
  back: {
    rotateY: 180,
    transition: easeOut
  }
} satisfies AnimationVariants;

export const cardInner = {
  front: { opacity: 1 },
  back: { opacity: 0 }
} satisfies AnimationVariants;

export const cardBackContent = {
  front: { opacity: 0 },
  back: { opacity: 1 }
} satisfies AnimationVariants;

// ============================================
// Card Navigation (swipe feel)
// ============================================

export const cardSwipe = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.3, ease: [0.33, 1, 0.68, 1] }
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -100 : 100,
    opacity: 0,
    transition: { duration: 0.2 }
  })
} satisfies AnimationVariants;

// ============================================
// Quiz Option States
// ============================================

export const quizOption = {
  idle: {
    scale: 1,
    backgroundColor: 'var(--color-surface-elevated, #FAF6EC)'
  },
  hover: {
    scale: 1.02,
    transition: { duration: 0.15 }
  },
  tap: {
    scale: 0.98
  },
  correct: {
    scale: [1, 1.05, 1],
    backgroundColor: 'var(--color-correct, #3E6B53)',
    transition: { duration: 0.3, ease: 'easeOut' }
  },
  incorrect: {
    x: [0, -8, 8, -8, 8, 0],
    backgroundColor: 'var(--color-incorrect, #9A4848)',
    transition: { duration: 0.4 }
  },
  disabled: {
    opacity: 0.5,
    scale: 1
  }
} satisfies AnimationVariants;

// ============================================
// Particle Quiz Option
// ============================================

export const particleOption = {
  idle: {
    scale: 1,
    borderColor: '#D9CDB1',
    backgroundColor: '#FAF6EC'
  },
  hover: {
    scale: 1.05,
    borderColor: '#9A4848',
    boxShadow: '0 6px 20px rgba(154, 72, 72, 0.3)',
    y: -3,
    transition: { duration: 0.2 }
  },
  tap: {
    scale: 0.95
  },
  correct: {
    borderColor: '#3E6B53',
    backgroundColor: 'rgba(62, 107, 83, 0.22)',
    scale: [1, 1.1, 1],
    transition: { duration: 0.6, ease: 'easeOut' }
  },
  incorrect: {
    borderColor: '#9A4848',
    backgroundColor: 'rgba(154, 72, 72, 0.22)',
    x: [0, -5, 5, -5, 5, 0],
    transition: { duration: 0.4 }
  }
} satisfies AnimationVariants;

// ============================================
// Modal Animations
// ============================================

export const modalOverlay = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15 }
  }
} satisfies AnimationVariants;

export const modalContent = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: easeInOut
  },
  exit: {
    opacity: 0,
    y: 10,
    scale: 0.98,
    transition: { duration: 0.15 }
  }
} satisfies AnimationVariants;

// ============================================
// Toast Notifications
// ============================================

export const toast = {
  initial: {
    opacity: 0,
    y: -20,
    scale: 0.9
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: spring
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2 }
  }
} satisfies AnimationVariants;

// ============================================
// Progress Bar Fill
// ============================================

export const progressFill = {
  initial: { scaleX: 0, originX: 0 },
  animate: (width: number) => ({
    scaleX: width / 100,
    transition: { duration: 0.6, ease: [0.33, 1, 0.68, 1] }
  })
} satisfies AnimationVariants;

// ============================================
// Level Selector
// ============================================

export const levelOption = {
  inactive: {
    scale: 1,
    opacity: 0.7
  },
  active: {
    scale: 1.05,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 20 }
  }
} satisfies AnimationVariants;

// ============================================
// Speed Challenge Timer (urgency animation)
// ============================================

export const timerPulse = {
  normal: {
    scale: 1,
    color: 'var(--color-text, #1F2A3A)'
  },
  warning: {
    scale: [1, 1.1, 1],
    color: 'var(--color-warning, #D4A14A)',
    transition: {
      scale: { repeat: Infinity, duration: 0.5 }
    }
  },
  danger: {
    scale: [1, 1.15, 1],
    color: 'var(--color-incorrect, #9A4848)',
    transition: {
      scale: { repeat: Infinity, duration: 0.3 }
    }
  }
} satisfies AnimationVariants;

// ============================================
// Particle Quiz Blank (attention pulse)
// ============================================

export const blankPulse = {
  idle: {
    boxShadow: '0 0 0 2px var(--color-srs-review, #9A4848)'
  },
  focus: {
    boxShadow: [
      '0 0 0 2px var(--color-srs-review, #9A4848)',
      '0 0 0 4px rgba(154, 72, 72, 0.3)',
      '0 0 0 2px var(--color-srs-review, #9A4848)'
    ],
    transition: { repeat: Infinity, duration: 1.5 }
  },
  filled: {
    boxShadow: '0 0 0 2px var(--color-correct, #3E6B53)',
    transition: { duration: 0.2 }
  }
} satisfies AnimationVariants;

// ============================================
// SRS State Indicator
// ============================================

export const srsIndicator = {
  new: { backgroundColor: 'var(--color-srs-new, #A8C0CC)' },
  learning: { backgroundColor: 'var(--color-srs-learning, #C9A24A)' },
  review: { backgroundColor: 'var(--color-srs-review, #9A4848)' },
  mastered: {
    backgroundColor: 'var(--color-srs-mastered, #6F9B8E)',
    scale: [1, 1.2, 1],
    transition: { scale: { duration: 0.3 } }
  }
} satisfies AnimationVariants;

// ============================================
// Audio Button Feedback
// ============================================

export const audioButton = {
  idle: { scale: 1 },
  playing: {
    scale: [1, 1.1, 1],
    transition: { repeat: Infinity, duration: 0.6 }
  },
  loading: { opacity: 0.6 }
} satisfies AnimationVariants;

// ============================================
// Tab Switching
// ============================================

export const tabContent = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.15 }
  }
} satisfies AnimationVariants;

// ============================================
// Stats Counter (number tick up)
// ============================================

export const counterTick = {
  initial: {
    opacity: 0,
    y: 10
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 20 }
  }
} satisfies AnimationVariants;

// ============================================
// Flashcard Container (study mode)
// ============================================

export const flashcardContainer = {
  initial: { opacity: 0, scale: 0.95 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.3, ease: 'easeOut' }
  }
} satisfies AnimationVariants;

// ============================================
// Menu Dropdown
// ============================================

export const dropdown = {
  closed: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    pointerEvents: 'none'
  },
  open: {
    opacity: 1,
    y: 0,
    scale: 1,
    pointerEvents: 'auto',
    transition: { duration: 0.2, ease: 'easeOut' }
  }
} satisfies AnimationVariants;

// ============================================
// Stagger Children (for lists)
// ============================================

export const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
} satisfies AnimationVariants;

export const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2 }
  }
} satisfies AnimationVariants;

// ============================================
// Feedback Icons (checkmark/X)
// ============================================

export const feedbackIcon = {
  hidden: {
    scale: 0,
    opacity: 0
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 25,
      delay: 0.1
    }
  }
} satisfies AnimationVariants;
